import React, { useEffect } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Provider } from 'react-redux'
import { Authenticator } from '@aws-amplify/ui-react'
import { store } from '../../store'
import CustomTheme from '../../theme'
import { SnackbarProvider } from 'notistack'
import { Notifications, LoadingNotifications, flushFlashNotifications } from '../Notifications'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { type AxiosError } from 'axios'
import ErrorBoundary from '../ErrorBoundary'
import { SetupTourProvider } from '../../features/SetupWizardFeature/SetupWizardProvider'
import { AuthenticatedProvider } from '../../hooks/AuthenticatedContext'
import { ReactQueryProvider } from './ReactQueryProvider'
import { GlobalSlotTaskPoller } from '../GlobalSlotTaskPoller'

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: AxiosError
  }
}

interface ProvidersProps {
  children: React.ReactNode
}

const SnackbarComponents = {
  info: Notifications,
  success: Notifications,
  warning: Notifications,
  error: Notifications,
  loading: LoadingNotifications
}

/**
 * Re-shows any notifications queued via `flashNotification` before a reload.
 * Rendered inside `SnackbarProvider` so the imperative enqueue API is ready.
 */
const FlashNotificationsFlusher: React.FC = () => {
  useEffect(() => {
    flushFlashNotifications()
  }, [])
  return null
}

const Providers: React.FC<ProvidersProps> = ({ children }): JSX.Element => {
  return (
    <>
      <Provider store={store}>
        <ErrorBoundary>
          <ThemeProvider theme={CustomTheme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
            <SnackbarProvider maxSnack={3}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
              Components={SnackbarComponents}>
              <FlashNotificationsFlusher />
              <Authenticator.Provider>
                <AuthenticatedProvider>
                  <SetupTourProvider>
                    <ReactQueryProvider>
                      {children}
                      <GlobalSlotTaskPoller />
                      <ReactQueryDevtools initialIsOpen={false} />
                    </ReactQueryProvider>
                  </SetupTourProvider>
                </AuthenticatedProvider>
              </Authenticator.Provider>
            </SnackbarProvider>
            </LocalizationProvider>
          </ThemeProvider>
        </ErrorBoundary>
      </Provider>
    </>
  )
}

export default Providers
